import * as React from 'react'
import {ReactNode} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {HttpError} from '../../../network/errors/HttpError'
import {HttpTimeoutError} from '../../../network/errors/baseHandling/HttpTimeoutError'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import {createIconWithLevel} from '../../../styledComponents/adminPage/IconCreator'
import {Box, SnackbarContent} from '@mui/material';
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import EventEmitterExAdmin from './EventEmitterExAdmin'
import {FontWeights} from '../../../styledComponents/Constants'
import styled from 'styled-components'

export interface HeadingAreaMessageParams {
    messageLevel: MESSAGE_TYPE
    title: Nullable<string>
    detail: string
    itemErrors?: string[]
}

interface HeadingAreaMessageComponentProps {
    eventEmitter?: EventEmitterExAdmin
    params?: HeadingAreaMessageParams
}

interface HeadingAreaMessageComponentState {
    params: Nullable<HeadingAreaMessageParams>
}

export class HeadingAreaMessageComponent
    extends React.Component<HeadingAreaMessageComponentProps, HeadingAreaMessageComponentState> {

    constructor(props: Readonly<HeadingAreaMessageComponentProps>) {
        super(props)

        this.state = {
            params: null,
        }
        this.handleShowMessage = this.handleShowMessage.bind(this)
        this.handleClearMessage = this.handleClearMessage.bind(this)
    }

    componentDidMount(): void {
        if (this.props.params) {
            this.setState({
                params: this.props.params,
            })
        }
        if (!this.props.eventEmitter) {
            return
        }
        this.props.eventEmitter.on('showHeadingAreaMessage', this.handleShowMessage)
        this.props.eventEmitter.on('clearHeadingAreaMessage', this.handleClearMessage)
    }

    componentWillUnmount(): void {
        if (!this.props.eventEmitter) {
            return
        }
        this.props.eventEmitter.off('showHeadingAreaMessage', this.handleShowMessage)
        this.props.eventEmitter.off('clearHeadingAreaMessage', this.handleClearMessage)
    }


    handleShowMessage(
        paramsOrError: HeadingAreaMessageParams | HttpError | HttpTimeoutError,
        includeItemErrors: boolean,
    ): void {
        if (!paramsOrError) {
            return
        }
        const isHeadingAreaMessageParams = (target: object) => (target as BaseHttpError).status === undefined

        const convertToHeadingAreaMessageParams = (error: HttpError): HeadingAreaMessageParams => {
            const {title, type, message, messageId} = error.responseBody.requestError
            const {requestItemErrors} = error.responseBody
            const itemErrors = includeItemErrors ? requestItemErrors.map(item => item.message) : []
            showParams = {
                title: title,
                messageLevel: type,
                detail: `${messageId}: ${message}`,
                itemErrors: itemErrors,
            }
            return showParams
        }

        let showParams: HeadingAreaMessageParams
        if (isHeadingAreaMessageParams(paramsOrError)) {
            showParams = (paramsOrError as HeadingAreaMessageParams)
        } else {
            showParams = convertToHeadingAreaMessageParams(paramsOrError as HttpError)
        }
        this.setState({params: showParams}, () => {
            if (!this.props.eventEmitter) {
                return
            }
            this.props.eventEmitter.scrollToTop()
        })
    }

    handleClearMessage(): void {
        this.setState({params: null})
    }

    render(): ReactNode {
        const {params} = this.state
        if (!params) {
            return null
        }
        if (params.title) {
            return (
                <HeadingAreaMessage
                    messageLevel={params.messageLevel}
                    title={params.title}
                    detail={params.detail}
                    itemErrors={params.itemErrors}
                />
            )

        } else {
            return (
                <HeadingAreaMessage
                    messageLevel={params.messageLevel}
                    title={params.detail}
                    detail={null}
                    itemErrors={params.itemErrors}
                />
            )
        }
    }
}


interface Props {
    messageLevel: MESSAGE_TYPE
    title: string
    detail: Nullable<string>
    itemErrors?: string[]
}

const HeadingAreaMessage: React.FC<Props> = props => {
    const judgeColor = (level?: any) => {

        let normalized: MESSAGE_TYPE | undefined;
        if (level instanceof MESSAGE_TYPE) {
            normalized = level;
        } else {
            switch (level?.label) {
                case 'information':
                    normalized = MESSAGE_TYPE.INFORMATION;
                    break;
                case 'error':
                    normalized = MESSAGE_TYPE.ERROR;
                    break;
                case 'warning':
                    normalized = MESSAGE_TYPE.WARNING;
                    break;
                default:
                    normalized = undefined
            }
        }
        switch (normalized) {
            case MESSAGE_TYPE.INFORMATION:
                return '#00838a'
            case MESSAGE_TYPE.ERROR:
                return '#d7374a'
            case MESSAGE_TYPE.WARNING:
                return '#f5a623'
            default:
                return 'inherit'
        }
    }

    const icon: ReactNode = createIconWithLevel(props.messageLevel)
    const {title, detail, itemErrors} = props
    const color = judgeColor(props.messageLevel)
    return (
        <Box mb={'10px'}>
            <SnackbarContent
                style={{backgroundColor: color, color: 'white', fontWeight: FontWeights.Bold}}
                message={
                    <>
                        <LeftRowFlexContainer>
                            {icon}<StyledBox ml={'5px'}>{title}</StyledBox>
                        </LeftRowFlexContainer>
                        {
                            detail &&
                            <Box ml={'30px'}>{detail}</Box>
                        }
                        {
                            itemErrors &&
                            <StyledUl>
                                {itemErrors.map((item, index) => <li key={index}>{item}</li>)}
                            </StyledUl>
                        }
                    </>
                }
            />
        </Box>
    )
}

const StyledUl = styled.ul`
  margin: 0 0 0 0;
  padding: 5px 0 0 50px;
`

const StyledBox = styled(Box)`
  white-space: pre-wrap;
`
